import { createSelector } from '@reduxjs/toolkit'
import { NetSuiteAsset, NetSuiteAssetProduct, ContractLineDetails } from 'types'
import type { NetSuiteAssetManagerState } from 'store/netSuiteAssetManager/reducer'

export const selectNetSuiteAssetById = createSelector(
  [
    (state: NetSuiteAssetManagerState) => state.netSuiteAssets,
    (_state: NetSuiteAssetManagerState, id: number) => id,
  ],
  (assets, id): NetSuiteAsset | undefined => (assets ? assets[id] : undefined),
)

export const selectAssetsByContractLineId = createSelector(
  [
    (state: NetSuiteAssetManagerState) => state.netSuiteAssets,
    (_state: NetSuiteAssetManagerState, contractLineId: number) =>
      contractLineId,
  ],
  (assets, contractLineId): NetSuiteAsset[] => {
    if (!assets) return []

    return Object.values(assets).filter(
      asset => asset.contractLineNSId === contractLineId,
    )
  },
)

export const selectContractLineDetails = createSelector(
  [selectAssetsByContractLineId],
  (assets): ContractLineDetails => {
    if (!assets || assets.length === 0) {
      return {
        assignedAssets: [],
        unassignedAssets: [],
        products: [],
        contractLineId: null,
        managerNetSuiteCustomerId: null,
        startDate: null,
        expirationDate: null,
        setupAdminEmailUserId: null,
        managerNetSuiteCustomerName: null,
        managerNetSuiteCustomerNSId: null,
      }
    }

    const assignedAssets = assets.filter(asset => asset.site !== null)
    const unassignedAssets = assets.filter(asset => asset.site === null)

    const uniqueProducts = new Map<number, NetSuiteAssetProduct>()
    assets.forEach(asset => {
      if (asset.product && !uniqueProducts.has(asset.product.id)) {
        uniqueProducts.set(asset.product.id, asset.product)
      }
    })

    const firstAsset = assets[0]

    return {
      assignedAssets,
      unassignedAssets,
      products: Array.from(uniqueProducts.values()),
      contractLineId: firstAsset.contractLineNSId,
      managerNetSuiteCustomerId: firstAsset.managerNetSuiteCustomerId,
      startDate: firstAsset.startDate,
      expirationDate: firstAsset.expirationDate,
      setupAdminEmailUserId: null,
      managerNetSuiteCustomerName: null,
      managerNetSuiteCustomerNSId: null,
    }
  },
)
